import React, { useState } from 'react';
import './Contact.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    marina: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.marina
            ? `Location: ${formData.marina}\n\n${formData.message}`
            : formData.message
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          phone: '',
          email: '',
          marina: '',
          message: ''
        });
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Get in touch - we're here to help with all your marine service needs</p>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="contact-layout">
            <div className="contact-info">
              <div className="contact-card">
                <h2>Get In Touch</h2>
                <p>Ready to schedule service or have questions? We'd love to hear from you.</p>
                
                <div className="contact-methods">
                  <div className="contact-method">
                    <div className="method-icon">📞</div>
                    <div className="method-details">
                      <h3>Phone</h3>
                      <a href="tel:8503230769">850-323-0769</a>
                      <p>Call or text for fastest response</p>
                    </div>
                  </div>

                  <div className="contact-method">
                    <div className="method-icon">✉️</div>
                    <div className="method-details">
                      <h3>Email</h3>
                      <a href="mailto:Jeremy@islandmobilemarine.com">Jeremy@islandmobilemarine.com</a>
                      <p>We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="contact-method">
                    <div className="method-icon">⏰</div>
                    <div className="method-details">
                      <h3>Hours</h3>
                      <p>Monday - Saturday: 8am - 6pm</p>
                      <p>Sunday: By appointment</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="quick-links-card">
                <h3>Quick Links</h3>
                <div className="quick-links">
                  <a href="/services" className="quick-link">View Services</a>
                  <a href="/booking" className="quick-link">Book Online</a>
                  <a href="/about" className="quick-link">About Us</a>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h2>Message Sent!</h2>
                  <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                  <p className="success-contact">For urgent matters, call us at <a href="tel:8503230769">850-323-0769</a></p>
                </div>
              ) : (
                <>
                  <h2>Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="phone">Phone *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          pattern="[\d\s\-\(\)\+]{10,}"
                          title="Please enter a valid phone number (at least 10 digits)"
                          placeholder="850-323-0769"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                          title="Please enter a valid email address"
                          placeholder="you@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="marina">Marina / Location</label>
                      <input
                        type="text"
                        id="marina"
                        name="marina"
                        value={formData.marina}
                        onChange={handleChange}
                        placeholder="Where is your boat located?"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        required
                      />
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="faq-section">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>What areas do you service?</h3>
                <p>We provide mobile marine service throughout the local area. Contact us to confirm we service your marina or location.</p>
              </div>
              <div className="faq-item">
                <h3>How quickly can you come out?</h3>
                <p>We typically schedule service within 1-3 business days. Emergency service is available for urgent issues.</p>
              </div>
              <div className="faq-item">
                <h3>Do you provide estimates?</h3>
                <p>Yes! We provide free estimates for most services. We'll assess the work needed and give you a clear quote before starting.</p>
              </div>
              <div className="faq-item">
                <h3>What payment methods do you accept?</h3>
                <p>We accept cash, checks, and all major credit cards. Payment is due after service is completed.</p>
              </div>
              <div className="faq-item">
                <h3>Do I need to be present?</h3>
                <p>It's helpful but not always required. As long as your boat is accessible and we can reach you by phone, we can often work independently.</p>
              </div>
              <div className="faq-item">
                <h3>What if you need parts?</h3>
                <p>We'll let you know what parts are needed and provide options. We can often source and install parts the same day or next day.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
